import { useState, useCallback, useEffect } from 'react';
import ChatService from '../services/chatService';

/**
 * Custom hook for managing conversation messages and real-time subscriptions
 * Handles message loading, sending, and real-time updates
 */
export const useConversationMessages = (conversationId, onMessagesLoaded) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isAtBottom, setIsAtBottom] = useState(true);

  // Load messages for conversation
  const loadMessages = useCallback(async () => {
    if (!conversationId) return;

    try {
      setLoading(true);
      setError(null);
      console.log('Loading messages for conversation:', conversationId);
      
      const result = await ChatService.getConversationMessages(conversationId);
      
      if (result.success) {
        setMessages(result.data || []);
        onMessagesLoaded?.(result.data?.length);
      } else {
        setError('Failed to load messages');
      }
    } catch (err) {
      console.error('Failed to load messages:', err);
      setError(err.message || 'Failed to load messages');
    } finally {
      setLoading(false);
    }
  }, [conversationId, onMessagesLoaded]);

  // Send message
  const sendMessage = useCallback(
    async (messageText, userProfileId, isAdmin = true) => {
      if (!conversationId || !userProfileId || !messageText.trim()) {
        setError('Missing required fields');
        return { success: false };
      }

      const optimisticMessage = {
        id: `temp-${Date.now()}`,
        conversation_id: conversationId,
        sender_id: userProfileId,
        message_text: messageText.trim(),
        is_admin: isAdmin,
        created_at: new Date().toISOString(),
        sender: {
          id: userProfileId
        }
      };

      // Add optimistic message
      setMessages(prev => [...prev, optimisticMessage]);

      try {
        setError(null);
        console.log('Sending message:', { conversationId, isAdmin });

        const result = await ChatService.sendMessage(
          conversationId,
          userProfileId,
          messageText.trim(),
          isAdmin
        );

        if (result.success) {
          // Reload messages to get the real message with correct ID
          setTimeout(() => {
            loadMessages();
          }, 200);
          
          return { success: true };
        } else {
          // Remove optimistic message on failure
          setMessages(prev => prev.filter(m => m.id !== optimisticMessage.id));
          setError('Failed to send message');
          return { success: false, error: result.error };
        }
      } catch (err) {
        // Remove optimistic message on failure
        setMessages(prev => prev.filter(m => m.id !== optimisticMessage.id));
        console.error('Failed to send message:', err);
        setError(err.message || 'Failed to send message');
        return { success: false, error: err.message };
      }
    },
    [conversationId, loadMessages]
  );

  // Handle scroll position tracking
  const handleScroll = useCallback((containerElement) => {
    if (!containerElement) return;
    
    const { scrollTop, scrollHeight, clientHeight } = containerElement;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    setIsAtBottom(distanceFromBottom < 50);
  }, []);

  return {
    messages,
    loading,
    error,
    isAtBottom,
    loadMessages,
    sendMessage,
    handleScroll
  };
};
